import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { useThunk } from "../../hooks/useThunk";
import {
  createProducerPack,
  getSpecificProducerPack,
  updateProducerPack,
} from "../../services/thunks/producerPackThunks";
import { getBasicProducerCategoriesInfo } from "../../services/thunks/producerCategoryThunks";
import { getAllProducerTagsWithoutPagination } from "../../services/thunks/producerTagThunks";
import { uploadToCloudflare } from "../../store/thunks/cloudFlareThunks";
import showToast from "../../utils/toastNotifications";
import { validateTag } from "../../utils/validationUtils";
import { sanitizeHyphens } from "../../utils/formatting";
import { localDateAsUtcMidnight } from "../../utils/dateFormating";
import { generatePeaksFromURL } from "../../utils/generateSongPeeks";

import Input from "../../components/Form/Input";
import Dropdown from "../../components/Form/Dropdown";
import FileUpload from "../../components/Form/FileUpload";
import MultiSelectedDropDownWithInput from "../../components/Form/MultiSelectedDropDownWithInput";
import CustomButton from "../../components/Buttons/CustomButton";

const AddNewProducerPack = () => {
  const { id } = useParams();
  const auth = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [doGetCategories] = useThunk(getBasicProducerCategoriesInfo);
  const [doGetTags] = useThunk(getAllProducerTagsWithoutPagination);
  const [doUpdateProducerPack, isUpdatingProducerPack] = useThunk(updateProducerPack);
  const [doCreateProducerPack, isCreatingProducerPack] = useThunk(createProducerPack);
  const [doUploadToCloudflare, isUploadingToCloudflare] = useThunk(uploadToCloudflare);
  const [doGetProducerPackById] = useThunk(getSpecificProducerPack);

  const [packDetails, setPackDetails] = useState({
    title: "",
    volume: "",
    category: null,
    coverImage: null,
    tags: [],
    description: "",
    createdBy: "",
    socialLink: "",
    daw: "",
    plugin: "",
    previews: [],
    files: [{ rank: "", url: "" }],
    publishedDate: localDateAsUtcMidnight(),
  });

  const [categories, setCategories] = useState([]);
  const [tagsOptions, setTagsOptions] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [tagErrors, setTagErrors] = useState({});

  // Ensure the page scrolls to top when this component mounts
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch (e) {
      // ignore in environments without window
    }
  }, []);

  useEffect(() => {
    const fetchDropdownData = async () => {
      const [categoriesRes, tagsRes] = await Promise.all([
        doGetCategories({ token: auth.token }),
        doGetTags({ token: auth.token }),
      ]);

      const normalizedCategories =
        categoriesRes?.response?.data && Array.isArray(categoriesRes.response.data.items)
          ? categoriesRes.response.data.items
            .filter((c) => c.name.toLowerCase() !== "uncategorized")
            .map((c) => ({ _id: c._id, name: c.name }))
          : [];
      setCategories(normalizedCategories);

      const normalizedTags =
        tagsRes?.success === true && Array.isArray(tagsRes.response?.data?.items)
          ? tagsRes.response?.data.items
            .filter((t) => t.tagName.toLowerCase() !== "unavailable")
            .map((t) => ({
              _id: t._id,
              name: t.tagName,
            }))
          : [];
      setTagsOptions(normalizedTags);
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    if (id) {
      const fetchProducerPack = async () => {
        const res = await doGetProducerPackById({ id: id, token: auth.token });
        if (res?.success) {
          const packData = res.response.data;

          const normalizedTags = (packData.tags || []).map((tag) => ({
            _id: tag._id,
            name: tag.tagName,
            isCustom: false,
          }));

          // Normalize previews to array format
          let normalizedPreviews = [];
          if (Array.isArray(packData.previews)) {
            normalizedPreviews = packData.previews.map((preview) => {
              if (typeof preview === 'string') {
                return { name: "", audioFile: { url: preview } };
              }
              return {
                name: preview.name || "",
                audioFile: {
                  url: preview.url || "",
                  key: preview.key || "",
                  trackName: preview.trackName || "",
                  peaks: preview.peaks || [],
                  name: preview.trackName || preview.name || ""
                }
              };
            });
          } else if (packData.preview && typeof packData.preview === 'string') {
            // Backwards compatibility for single preview field
            normalizedPreviews = [{ name: "", audioFile: { url: packData.preview } }];
          }

          const normalizedPackLinks =
            Array.isArray(packData.files) && packData.files.length > 0
              ? packData.files.map((file) => ({
                rank: file?.rank ?? "",
                url: file?.url || "",
              }))
              : [{ rank: "", url: "" }];

          setPackDetails({
            ...packData,
            publishedDate: packData.publishedDate?.split("T")[0],
            tags: normalizedTags,
            previews: normalizedPreviews,
            files: normalizedPackLinks,
          });
        } else {
          showToast("error", "Failed to fetch producer pack");
        }
      };
      fetchProducerPack();
    }
  }, [id]);

  const validateField = (name, value) => {
    switch (name) {
      case "title":
        if (!value || !value.trim()) return "Title is required";
        if (value.length > 100) return "Title max 100 characters";
        break;
      // case "volume":
      //   if (value && !/^[1-9][0-9]{0,2}$/.test(value)) {
      //     return "Volume must be a positive integer (max 3 digits)";
      //   }
      //   break;
      case "description":
        if (value.length > 500) {
          return "Description max 500 characters";
        }
        break;
      case "coverImage":
        if (!value || (!value.url && !value.name)) {
          return "Cover image is required";
        }
        break;
      case "publishedDate":
        if (!value) {
          return "Published date is required";
        }
        break;
      default:
        return "";
    }
    return "";
  };

  const uploadCoverImage = async (file) => {
    const res = await doUploadToCloudflare({ file, type: "images" });
    const data = res.response;

    if (!data || !data.key || !data.url) {
      throw new Error("Image upload failed");
    }

    return {
      key: data.key,
      url: data.url,
    };
  };

  const uploadPreviewFile = async (file) => {
    const res = await doUploadToCloudflare({ file, type: "previews" });
    const data = res.response;

    if (!data || !data.key || !data.url) {
      throw new Error("Audio upload failed");
    }

    return {
      key: data.key,
      url: data.url,
      name: data.name || file.name,
      type: data.type || file.type,
    };
  };



  const handleSubmit = async () => {
    setIsLoading(true);

    const tagValidationErrors = packDetails.tags
      .map((tag) => validateTag(tag.name))
      .filter((msg) => msg);

    if (tagValidationErrors.length > 0) {
      setTagErrors(tagValidationErrors);
      showToast("error", "Please fix tag validation errors");
      setIsLoading(false);
      return;
    }

    if (hasFormErrors) {
      showToast("error", "Please fix form errors before submitting");
      setIsLoading(false);
      return;
    }

    // Only the URL is user-entered. Ranks are auto-assigned sequentially
    // (1, 2, 3, …) from row order below, so the ranks are always contiguous and
    // 1-based. This prevents non-sequential entries (e.g. 2, 3 or 1, 3) that
    // would otherwise break downloads.
    const enteredPackLinks = (packDetails.files || [])
      .map((link) => ({ url: String(link?.url ?? "").trim() }))
      .filter((link) => link.url !== "");

    if (enteredPackLinks.length === 0) {
      showToast("error", "At least one pack link with a URL is required");
      setIsLoading(false);
      return;
    }

    const sortedPackLinks = enteredPackLinks.map((link, index) => ({
      rank: String(index + 1),
      url: link.url,
    }));

    let uploadedCoverImage = packDetails.coverImage;

    if (
      packDetails.coverImage &&
      !packDetails.coverImage.url &&
      !packDetails.coverImage.key
    ) {
      console.log("packDetails.coverImage", packDetails.coverImage);
      uploadedCoverImage = await uploadCoverImage(packDetails.coverImage);

      if (!uploadedCoverImage?.url || !uploadedCoverImage?.key) {
        showToast("error", "Image upload failed");
        setIsLoading(false);
        return;
      }

      showToast("success", "Image upload successful");
    }

    if (!uploadedCoverImage?.url || !uploadedCoverImage?.key) {
      showToast("error", "Missing cover image");
      setIsLoading(false);
      return;
    }

    // Upload preview files to Cloudflare
    const uploadedPreviews = [];
    for (let i = 0; i < packDetails.previews.length; i++) {
      const preview = packDetails.previews[i];

      console.log(`Processing preview ${i + 1}:`, preview);

      // If preview has a file object (not uploaded yet)
      if (preview.audioFile && !preview.audioFile.url && !preview.audioFile.key) {
        const uploadedFile = await uploadPreviewFile(preview.audioFile);

        if (!uploadedFile) {
          setIsLoading(false);
          return;
        }

        // Generate peaks from the uploaded file
        const peaksRaw = await generatePeaksFromURL(preview.audioFile);
        const peaks = Array.isArray(peaksRaw[0]) ? peaksRaw[0] : peaksRaw;

        uploadedPreviews.push({
          name: preview.name || "",
          url: uploadedFile.url,
          key: uploadedFile.key,
          trackName: uploadedFile.name || preview.audioFile.name,
          peaks: peaks,
        });
      } else if (preview.audioFile?.url && preview.audioFile?.key) {
        // Already uploaded file
        uploadedPreviews.push({
          name: preview.name || "",
          url: preview.audioFile.url,
          key: preview.audioFile.key,
          trackName: preview.audioFile.trackName || preview.audioFile.name || "",
          peaks: preview.audioFile.peaks || [],
        });
      }
    }

    const updatedPackDetails = {
      ...packDetails,
      coverImage: uploadedCoverImage,
      previews: uploadedPreviews,
      files: sortedPackLinks.map((link) => ({
        rank: Number(link.rank),
        url: link.url,
      })),
      category: packDetails.category?._id || packDetails.category,
      tags: packDetails.tags.map((t) => ({
        name: t.name,
        isCustom: t.isCustom || false,
      })),
    };

    try {
      const effectiveId = id || packDetails?._id;
      if (effectiveId) {
        const res = await doUpdateProducerPack({
          payload: updatedPackDetails,
          id: effectiveId,
          token: auth.token,
        });
        if (res.success) {
          showToast("success", "Producer pack updated successfully");
          navigate("/manage-producer-packs");
        } else {
          showToast("error", res.error.message || "Error updating producer pack");
        }
      } else {
        const res = await doCreateProducerPack({
          payload: updatedPackDetails,
          token: auth.token,
        });
        if (res.success) {
          showToast("success", "Producer pack created successfully");
          navigate("/manage-producer-packs");
        } else {
          showToast("error", res.error.message || "Error creating producer pack");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      showToast("error", "Failed to process producer pack submission");
    } finally {
      setIsLoading(false);
    }
  };

  const hasFormErrors = Object.values(formErrors).some(
    (err) => err && err.trim() !== ""
  );



  const handlePreviewChange = (index, field, value) => {
    const updatedPreviews = [...packDetails.previews];
    updatedPreviews[index] = { ...updatedPreviews[index], [field]: value };
    setPackDetails({ ...packDetails, previews: updatedPreviews });
  };

  const addPreviewField = () => {
    setPackDetails({
      ...packDetails,
      previews: [...packDetails.previews, { name: "", audioFile: null }],
    });
  };

  const removePreviewField = (index) => {
    const updatedPreviews = packDetails.previews.filter((_, i) => i !== index);
    setPackDetails({ ...packDetails, previews: updatedPreviews });
  };

  const handlePackLinkChange = (index, field, value) => {
    const updatedLinks = [...(packDetails.files || [])];
    updatedLinks[index] = { ...updatedLinks[index], [field]: value };
    setPackDetails({ ...packDetails, files: updatedLinks });
  };

  const addPackLinkField = () => {
    setPackDetails({
      ...packDetails,
      files: [...(packDetails.files || []), { rank: "", url: "" }],
    });
  };

  const removePackLinkField = (index) => {
    const updatedLinks = (packDetails.files || []).filter((_, i) => i !== index);
    setPackDetails({
      ...packDetails,
      files: updatedLinks.length ? updatedLinks : [{ rank: "", url: "" }],
    });
  };

  return (
    <div className="min-h-screen p-10 text-white">
      <div className="bg-[#0D0D0D] rounded-lg border border-[#505050] p-6 mb-6">
        <div className="text-[20px] font-[700] font-Public_Sans text-white mb-6">
          Producer Pack Details
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Input
            label="Title"
            placeholder="Enter Title"
            value={packDetails.title}
            onChange={(e) => {
              const value = e.target.value;
              setPackDetails((prev) => ({ ...prev, title: value }));
              setFormErrors((prev) => ({ ...prev, title: null }));
            }}
            onBlur={() =>
              setFormErrors((prev) => ({
                ...prev,
                title: validateField("title", packDetails.title),
              }))
            }
            isRequired={true}
            errorMessage={formErrors.title}
            inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[45px]"
            outerContainerStyle="mt-[-1px]"
            labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
            placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
          />
          <Dropdown
            label="Category"
            placeholder="Select Category"
            options={categories}
            displayKey="name"
            idKey="_id"
            isRequired={true}
            defaultOption={packDetails.category}
            onSelect={(v) => {
              setPackDetails((prev) => ({ ...prev, category: v }));
              setFormErrors((prev) => ({ ...prev, category: null }));
            }}
            onBlur={() =>
              setFormErrors((prev) => ({
                ...prev,
                category: validateField("category", packDetails.category),
              }))
            }
            errorMessage={formErrors.category}
            wrapperClassName="text-[#E2E2E2] text-[15px] font-[300] font-['Public Sans'] w-full min-w-[281px]"
            dropdownClassName="bg-[#2D2D2D] border-[#808080] border z-20"
            optionClassName="text-[#E2E2E2] py-2"
            buttonClassName="h-[45px]"
            filterClassName="border-t border-[#505050]"
            labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
            placeholderClassName="text-[#808080] text-[14px] font-['Public Sans'] font-normal"
            isSearchable={true}
          />
          <FileUpload
            label="Cover Image"
            type="image"
            defaultValue={packDetails.coverImage}
            maxSize={5}
            isRequired={true}
            onFileUpload={(f) =>
              setPackDetails({ ...packDetails, coverImage: f })
            }
            onBlur={() =>
              setFormErrors((prev) => ({
                ...prev,
                coverImage: validateField("coverImage", packDetails.coverImage),
              }))
            }
            showErrorBelow={false}
            error={formErrors.coverImage}
            containerStyle="w-full min-w-[220px] w-full h-full "
            labelStyle="text-sm mb-[8px] font-medium font-['Public_Sans'] text-[#E3E3E3] whitespace-nowrap"
          />
          {/* <Input
            label="Pack Link"
            placeholder="Add Pack Link"
            value={packDetails.fileUrl}
            isRequired={true}
            onChange={(e) =>
              setPackDetails((prev) => ({ ...prev, fileUrl: e.target.value }))
            }
            onBlur={() =>
              setFormErrors((prev) => ({
                ...prev,
                fileUrl: validateField("fileUrl", packDetails.fileUrl),
              }))
            }
            inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[45px]"
            labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
            placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
          /> */}
          <div className="col-span-2">
            <MultiSelectedDropDownWithInput
              label="Tags"
              options={tagsOptions}
              defaultSelected={packDetails.tags}
              onSelect={(newTags) => {
                const cleanTags = newTags.map((t) => ({
                  ...t,
                  name: sanitizeHyphens(t.name),
                }));

                setPackDetails((prev) => ({ ...prev, tags: cleanTags }));

                const errors = cleanTags
                  .map((tag) => validateTag(tag.name))
                  .filter(Boolean);
                setTagErrors(errors);
              }}
              error={tagErrors.length > 0 ? tagErrors[0] : ""}
            />
          </div>
          <div className="col-span-2">
            <Input
              label="Description"
              type="textarea"
              value={packDetails.description}
              onChange={(e) =>
                setPackDetails({
                  ...packDetails,
                  description: e.target.value,
                })
              }
              onBlur={() =>
                setFormErrors((prev) => ({
                  ...prev,
                  description: validateField("description", packDetails.description),
                }))
              }
              errorMessage={formErrors.description}
              inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[120px]"
              outerContainerStyle="mt-[-1px]"
              labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
              placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
            />
          </div>
          <Input
            label="Created By"
            placeholder="Enter creator name"
            value={packDetails.createdBy}
            onChange={(e) =>
              setPackDetails({ ...packDetails, createdBy: e.target.value })
            }
            inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[45px]"
            outerContainerStyle="mt-[-1px]"
            labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
            placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
          />
          <Input
            label="Social Media Link"
            placeholder="Enter social media link"
            value={packDetails.socialLink}
            onChange={(e) =>
              setPackDetails({ ...packDetails, socialLink: e.target.value })
            }
            inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[45px]"
            outerContainerStyle="mt-[-1px]"
            labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
            placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
          />
          <Input
            type="number"
            label="Volume"
            placeholder="Vol. X"
            value={packDetails.volume}
            onChange={(e) =>
              setPackDetails({ ...packDetails, volume: e.target.value })
            }
            onBlur={() =>
              setFormErrors((prev) => ({
                ...prev,
                volume: validateField("volume", packDetails.volume),
              }))
            }
            errorMessage={formErrors.volume}
            inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[45px]"
            outerContainerStyle="mt-[-1px]"
            labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
            placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
          />
          <Input
            label="DAW"
            placeholder="Enter DAW (e.g., Ableton, FL Studio)"
            value={packDetails.daw}
            onChange={(e) =>
              setPackDetails({ ...packDetails, daw: e.target.value })
            }
            inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[45px]"
            outerContainerStyle="mt-[-1px]"
            labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
            placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
          />
          <Input
            label="Plugin"
            placeholder="Enter plugin name"
            value={packDetails.plugin}
            onChange={(e) =>
              setPackDetails({ ...packDetails, plugin: e.target.value })
            }
            inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[45px]"
            outerContainerStyle="mt-[-1px]"
            labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
            placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
          />
          <Input
            label="Published Date"
            type="date"
            value={packDetails.publishedDate}
            isRequired={true}
            onChange={(e) =>
              setPackDetails({
                ...packDetails,
                publishedDate: e.target.value,
              })
            }
            isLocalUTC={true}
            errorMessage={formErrors.publishedDate}
            inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[45px]"
            outerContainerStyle="mt-[-1px]"
            labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
            placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
          />
        </div>

        {/* Pack Links Section */}
        <div className="mt-6 rounded-lg border-[1px] border-[#505050] p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Pack Link</h3>
            <CustomButton
              className="h-[35px] text-[14px] font-normal font-inter"
              onClick={addPackLinkField}
            >
              + Add New
            </CustomButton>
          </div>
          {(packDetails.files || []).map((link, index) => (
            <div key={index} className="flex flex-col gap-2 mb-4">
              <div className="flex flex-col md:flex-row gap-4 items-start w-full">
                <div className="w-full md:w-1/2">
                  <Input
                    type="number"
                    label="Rank"
                    isRequired={true}
                    placeholder="Rank"
                    value={index + 1}
                    disabled={true}
                    readOnly={true}
                    onChange={() => {}}
                    inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[45px] opacity-60 cursor-not-allowed"
                    labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
                    placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
                  />
                </div>
                <div className="w-full md:w-1/2">
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Input
                        type="text"
                        label="Path"
                        isRequired={true}
                        placeholder="upload packs via a link(URL)"
                        value={link.url ?? ""}
                        onChange={(e) => handlePackLinkChange(index, "url", e.target.value)}
                        inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[45px]"
                        labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
                        placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
                      />
                    </div>
                    <CustomButton
                      className="h-[45px] text-[14px] font-normal font-inter mb-[-1px]"
                      textColor="text-[#C00707]"
                      borderColor="border-[#C00707]"
                      onClick={() => removePackLinkField(index)}
                    >
                      Remove
                    </CustomButton>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Previews Section */}
        <div className="mt-6 rounded-lg border-[1px] border-[#505050] p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">
              Upload Previews
            </h3>
            <CustomButton
              className="h-[35px] text-[14px] font-normal font-inter"
              onClick={addPreviewField}
            >
              + Add Previews
            </CustomButton>
          </div>
          {packDetails.previews.map((preview, index) => (
            <div key={index} className="flex flex-col gap-2 mb-4">
              <div className="flex flex-col md:flex-row gap-4 items-start w-full">
                <div className="w-full md:w-1/2">
                  <Input
                    label="Preview Name"
                    isRequired={true}
                    placeholder="Enter Preview Name"
                    value={preview.name || ""}
                    onChange={(e) => handlePreviewChange(index, "name", e.target.value)}
                    inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[45px]"
                    labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
                    placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
                  />
                </div>
                <div className="w-full md:w-1/2">
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <label className="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap mb-[8px]">
                        Audio File
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <FileUpload
                        label=""
                        isRequired
                        maxSize={100}
                        defaultValue={preview.audioFile}
                        uploadText="Select your file or drag and drop here"
                        type="audio"
                        onFileUpload={(file) => handlePreviewChange(index, "audioFile", file)}
                      />
                    </div>
                    <CustomButton
                      className="h-[45px] text-[14px] font-normal font-inter mb-[-1px]"
                      textColor="text-[#C00707]"
                      borderColor="border-[#C00707]"
                      onClick={() => removePreviewField(index)}
                    >
                      Remove
                    </CustomButton>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>


        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input
            label="File URL"
            placeholder="Enter file URL"
            value={packDetails.fileUrl}
            isRequired={true}
            onChange={(e) =>
              setPackDetails((prev) => ({ ...prev, fileUrl: e.target.value }))
            }
            onBlur={() =>
              setFormErrors((prev) => ({
                ...prev,
                fileUrl: validateField("fileUrl", packDetails.fileUrl),
              }))
            }
            inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[45px]"
            labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
            placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
          />
        </div> */}
      </div>

      <div className="flex justify-center gap-4 mt-6">
        <CustomButton
          textColor="text-[#818181] h-[45px] w-[145px]"
          className="text-[18px] font-jost"
          borderColor="border-[#818181] border-[2px]"
          onClick={() => navigate("/manage-producer-packs")}
        >
          Cancel
        </CustomButton>
        <CustomButton
          useGradientBorder
          className="h-[45px] w-[145px] text-[18px] font-jost"
          textColor="gradient"
          gradient="bg-gradient-to-b from-[#52FAF0] to-[#197AD0]"
          onClick={handleSubmit}
          disabled={
            isLoading ||
            hasFormErrors ||
            isUpdatingProducerPack ||
            isCreatingProducerPack ||
            isUploadingToCloudflare ||
            tagErrors.length > 0
          }
          loading={
            isLoading ||
            isUpdatingProducerPack ||
            isCreatingProducerPack ||
            isUploadingToCloudflare
          }
        >
          <span>{isLoading ? "Saving..." : "Save Pack"}</span>
        </CustomButton>
      </div>
    </div>
  );
};

export default AddNewProducerPack;
